import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorLogAnalysisReportService } from '../../../../business-services/error-log-analysis-report.service';
import { ErrorLogAnalysisModel, ErrorLogOccurrenceListModel, ErrorLogOccurrenceModel, ErrorLogStatus } from '../../../../business-entities/error-log-analysis-report.model';
import { formatJson, getAnalysisRequest, getCauseText, getReportRange, getStatusOption, getThrownAt, toLocalDate } from '../error-log-analysis.helper';

/**
 * One Controller + Method: its current output (latest spike analysis) and all its occurrences in the report range.
 * Opened from the report as an EditPopup modal, like the other report detail popups.
 */
@Component({
  selector: 'app-error-log-endpoint-details',
  templateUrl: './error-log-endpoint-details.component.html',
  styleUrls: ['../../report-dashboard.css', '../error-log-shared.css', './error-log-endpoint-details.component.css']
})
export class ErrorLogEndpointDetailsComponent implements OnInit {

  readonly occurrencePageSize: number = 50;

  @Input() controllerName: string = '';
  @Input() methodName: string = '';
  @Input() dateRange: Date[] = [];

  // Tells the report to reload when a status was changed here.
  private statusChanged: boolean = false;

  spikes: ErrorLogAnalysisModel[] = [];
  latest: ErrorLogAnalysisModel = null;

  // Occurrences of the whole report range.
  occurrences: ErrorLogOccurrenceModel[] = [];
  occurrenceTotal: number = 0;
  occurrenceLoading: boolean = false;
  openOccurrenceId: number = null;
  private occurrenceRequestNo: number = 0;

  readonly toLocalDate = toLocalDate;
  readonly getCauseText = getCauseText;
  readonly getThrownAt = getThrownAt;
  readonly formatJson = formatJson;

  constructor(
    private service: ErrorLogAnalysisReportService,
    private spinner: NgxSpinnerService,
    private activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    this.loadAll();
  }

  OnClose() {
    this.activeModal.close({ success: true, statusChanged: this.statusChanged });
  }

  async loadAll() {
    this.spinner.show();
    await Promise.all([this.loadSpikes(), this.loadOccurrences(false)]).finally(() => {
      this.spinner.hide();
    });
  }

  async loadSpikes() {
    await this.service.GetErrorLogAnalysis(getAnalysisRequest(this.dateRange)).then((res: any) => {
      let data: ErrorLogAnalysisModel[] = Array.isArray(res?.responseObject) ? res.responseObject : [];
      // The API returns newest first, so the first spike is the current output.
      this.spikes = data.filter((item) => item.controllerName === this.controllerName && item.methodName === this.methodName);
      this.latest = this.spikes.length ? this.spikes[0] : null;
    });
  }

  async loadOccurrences(append: boolean) {
    let requestNo = ++this.occurrenceRequestNo;
    if (!append) {
      this.occurrences = [];
      this.occurrenceTotal = 0;
      this.openOccurrenceId = null;
    }
    let range = getReportRange(this.dateRange);
    this.occurrenceLoading = true;
    await this.service.GetErrorLogOccurrences({
      controllerName: this.controllerName,
      methodName: this.methodName,
      ...range,
      skip: this.occurrences.length,
      take: this.occurrencePageSize
    }).then((res: any) => {
      if (requestNo !== this.occurrenceRequestNo) {
        return;
      }
      let data: ErrorLogOccurrenceListModel = res?.responseObject;
      this.occurrences = this.occurrences.concat(data?.items || []);
      this.occurrenceTotal = data?.totalCount || 0;
    }).finally(() => {
      if (requestNo === this.occurrenceRequestNo) {
        this.occurrenceLoading = false;
      }
    });
  }

  // Infinite scroll of the occurrence table.
  onOccurrencesScrolled() {
    if (!this.occurrenceLoading && this.occurrences.length < this.occurrenceTotal) {
      this.loadOccurrences(true);
    }
  }

  toggleOccurrence(id: number) {
    this.openOccurrenceId = this.openOccurrenceId === id ? null : id;
  }

  trackById(index: number, item: ErrorLogOccurrenceModel): number {
    return item.id;
  }

  // ---------- Status ----------
  getStatus(status: ErrorLogStatus) {
    return getStatusOption(status);
  }

  onStatusSaved() {
    this.statusChanged = true;
    this.loadAll();
  }
}
